import { Trans } from '@lingui/react/macro';
import { BookingRecordListForCustomer } from '@navio/nsdk';
import { PaginatedResponse } from 'noddi-types';
import { NoddiButton, NoddiLink } from 'noddi-ui-web';
import { BookingsListTabs, customerRoutes } from 'noddi-util';
import { BookingCard } from './BookingCard';
import { DEFAULT_NUMBER_OF_BOOKINGS_SHOWN } from './constants';

const EXPAND_THRESHOLD = 3; // If more than this many extra bookings, navigate to dedicated page

type BookingsSectionProps = {
  bookingsPaginated: PaginatedResponse<BookingRecordListForCustomer[]>;
  isUpcomingBooking?: boolean;
  showAllBookings: boolean;
  setShowAllBookings: React.Dispatch<React.SetStateAction<boolean>>;
};

export const BookingsSection = ({
  bookingsPaginated,
  isUpcomingBooking,
  showAllBookings,
  setShowAllBookings
}: BookingsSectionProps) => {
  const bookings = bookingsPaginated.results;
  const totalBookings = bookingsPaginated.count;
  const isMoreBookings = totalBookings > DEFAULT_NUMBER_OF_BOOKINGS_SHOWN;
  const remainingBookings = totalBookings - DEFAULT_NUMBER_OF_BOOKINGS_SHOWN;
  const shouldNavigateToPage = remainingBookings > EXPAND_THRESHOLD;

  if (!bookings.length) {
    return null;
  }

  const bookingsPageTab = isUpcomingBooking ? BookingsListTabs.UPCOMING : BookingsListTabs.PREVIOUS;

  return (
    <div className='flex flex-col gap-2 md:gap-4'>
      <h2 className='text-xl font-bold text-primary md:text-3xl'>
        {isUpcomingBooking ? <Trans>Upcoming bookings</Trans> : <Trans>Previous bookings</Trans>}
      </h2>
      <div className='flex flex-col gap-3'>
        {bookings.map((booking) => (
          <BookingCard key={booking.id} isUpcomingBooking={isUpcomingBooking} booking={booking} />
        ))}
      </div>
      {isMoreBookings && (
        <div className='mt-1 flex w-full justify-between px-5'>
          {!showAllBookings && (
            <p className='text-xl font-semibold'>
              {`+ ${remainingBookings}`} <Trans>more</Trans>
            </p>
          )}
          {shouldNavigateToPage ? (
            <NoddiLink
              variant='outline'
              size='small'
              className='ml-auto'
              to={customerRoutes.bookings.getPath({ tab: bookingsPageTab })}
            >
              <Trans>Show all</Trans>
            </NoddiLink>
          ) : (
            <NoddiButton
              onClick={() => setShowAllBookings((prev) => !prev)}
              variant='outline'
              size='small'
              className='ml-auto'
            >
              {showAllBookings ? <Trans>Show less</Trans> : <Trans>Show all</Trans>}
            </NoddiButton>
          )}
        </div>
      )}
    </div>
  );
};
